"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, ShieldCheck, Snowflake, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const highlights = [{
  title: "Regional Service",
  desc: "Serving our listed Southern California and Las Vegas-area communities."
}, {
  title: "Trailer Options",
  desc: "Choose from 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a 53-foot reefer trailer. Availability and requirements for the 53-foot reefer are confirmed individually."
}, {
  title: "Quote and Scheduling",
  desc: "Availability, pricing, rental terms, and delivery scheduling are confirmed with each quote"
}];
export default function Booking() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="call-to-action" data-nav="light" className="relative bg-background py-16 md:py-24 overflow-hidden border-t border-border">
      {/* Subtle industrial background accent pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute -top-24 -right-24 size-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-accent/20 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative max-w-5xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="bg-card rounded-xl border border-border p-6 sm:p-12 md:p-16 text-center shadow-lg relative">
          {/* Status pill badge */}
          <div className="inline-flex items-center justify-center gap-2 px-3.5 py-1.5 rounded-full bg-muted border border-border text-foreground text-xs font-semibold tracking-wide uppercase mb-6 max-w-full whitespace-normal text-center leading-relaxed">
            <span className="size-2 rounded-full bg-primary animate-pulse shrink-0" />
            <span className="break-words">Mobile Cold Storage Rentals</span>
          </div>

          {/* Heading */}
          <h2 className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground max-w-2xl mx-auto leading-tight">Need Mobile Cold Storage for Your Dates?</h2>

          {/* Subtitle */}
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">Tell us your delivery location, requested dates, intended use, temperature requirements, preferred trailer size, site-access details, and available power so we can prepare a custom quote.</p>

          {/* Action CTA Buttons */}
          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 rounded-md shadow-sm transition-all">
              <Link to="/ContactRequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                <span>Request a Quote</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <a href="tel:2135218821" className="w-full sm:w-auto h-12 px-7 text-base font-semibold bg-muted text-muted-foreground rounded-md border border-border inline-flex items-center justify-center gap-2">
              <span>213-521-8821</span>
            </a>
          </div>

          {/* Value Micro-grid */}
          <div className="mt-12 pt-10 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
            {highlights.map((item, i) => <div key={item.title} data-index={i} className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-border/60">
                <div className="mt-0.5 size-8 shrink-0 rounded-md bg-muted flex items-center justify-center text-foreground">
                  {i === 0 && <SafeHydrate><Calendar className="size-4 text-primary" /></SafeHydrate>}
                  {i === 1 && <Snowflake className="size-4 text-primary" />}
                  {i === 2 && <ShieldCheck className="size-4 text-primary" />}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground font-default">
                    {item.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}